/**
 * WCAG colour maths for the places where a zone palette becomes readable UI.
 *
 * Zone palettes describe a room's light, not an accessibility contract. Keeping
 * this here lets a surface keep the room's hue without asking every UI caller to
 * rediscover the same luminance and alpha-compositing rules.
 */

const BLACK = 0x000000;
const WHITE = 0xffffff;
const BINARY_SEARCH_STEPS = 24;

interface Rgb {
  red: number;
  green: number;
  blue: number;
}

interface Hsl {
  hue: number;
  saturation: number;
  lightness: number;
}

function rgb(value: number): Rgb {
  return {
    red: (value >> 16) & 0xff,
    green: (value >> 8) & 0xff,
    blue: value & 0xff,
  };
}

function colour({ red, green, blue }: Rgb): number {
  return (red << 16) | (green << 8) | blue;
}

function srgbChannelToLinear(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.04045
    ? srgb / 12.92
    : ((srgb + 0.055) / 1.055) ** 2.4;
}

function luminanceChannel(channel: number, weight: number): number {
  return srgbChannelToLinear(channel) * weight;
}

function toHsl(value: number): Hsl {
  const { red, green, blue } = rgb(value);
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const maximum = Math.max(r, g, b);
  const minimum = Math.min(r, g, b);
  const chroma = maximum - minimum;
  const lightness = (maximum + minimum) / 2;

  if (chroma === 0) return { hue: 0, saturation: 0, lightness };

  let hue: number;
  if (maximum === r) hue = ((g - b) / chroma) % 6;
  else if (maximum === g) hue = (b - r) / chroma + 2;
  else hue = (r - g) / chroma + 4;

  return {
    hue: ((hue * 60) + 360) % 360,
    saturation: chroma / (1 - Math.abs((2 * lightness) - 1)),
    lightness,
  };
}

function fromHsl({ hue, saturation, lightness }: Hsl): number {
  const chroma = (1 - Math.abs((2 * lightness) - 1)) * saturation;
  const sector = hue / 60;
  const second = chroma * (1 - Math.abs((sector % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;

  if (sector < 1) [red, green] = [chroma, second];
  else if (sector < 2) [red, green] = [second, chroma];
  else if (sector < 3) [green, blue] = [chroma, second];
  else if (sector < 4) [green, blue] = [second, chroma];
  else if (sector < 5) [red, blue] = [second, chroma];
  else [red, blue] = [chroma, second];

  const match = lightness - (chroma / 2);
  return colour({
    red: Math.round((red + match) * 255),
    green: Math.round((green + match) * 255),
    blue: Math.round((blue + match) * 255),
  });
}

/** WCAG 2.1 relative luminance, including its sRGB transfer function. */
export function relativeLuminance(value: number): number {
  const { red, green, blue } = rgb(value);
  return luminanceChannel(red, 0.2126)
    + luminanceChannel(green, 0.7152)
    + luminanceChannel(blue, 0.0722);
}

/** WCAG 2.1 contrast ratio, from 1:1 (identical) to 21:1 (black and white). */
export function contrastRatio(first: number, second: number): number {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
}

/** Composite an opaque palette colour over another opaque colour in CSS sRGB. */
export function compositeOver(foreground: number, background: number, alpha: number): number {
  const front = rgb(foreground);
  const back = rgb(background);
  return colour({
    red: Math.round((front.red * alpha) + (back.red * (1 - alpha))),
    green: Math.round((front.green * alpha) + (back.green * (1 - alpha))),
    blue: Math.round((front.blue * alpha) + (back.blue * (1 - alpha))),
  });
}

/**
 * Move `foreground` only as far as needed to meet `minimum` against `background`.
 *
 * The hue and saturation stay fixed while HSL lightness moves toward the end
 * (white or black) that has more contrast with the background. Twenty-four
 * binary-search iterations bound the mix factor within 6e-8; the returned RGB is
 * still 8-bit, so further precision cannot change a CSS colour.
 *
 * **An unreachable minimum returns the best colour there is; it does NOT throw**,
 * and that is a deliberate reversal of how this shipped. A mid-tone background
 * has no 7:1 foreground at all — at luminance 0.18 both pure white and pure black
 * sit near 4.5:1 — so a future zone with a mid-grey `shadow` is not a strange
 * input, it is an ordinary one. `PauseOverlay` builds its colours in a getter
 * called from `attach()`, and `attach()` runs during BOOT rather than on the
 * first pause, so a throw here did not degrade the pause menu: it failed the
 * whole game to start, for a contrast target. An independent review found that
 * before any such palette existed.
 *
 * The promise is therefore "as close to `minimum` as this background allows",
 * and the ENFORCEMENT is the gate: `pause.spec.ts` measures the achieved ratio
 * for every zone in `ZONES` off the browser's computed styles and fails if any
 * falls short. A palette that cannot be made readable is caught at build time
 * with the zone named, instead of at the player's launch with a blank screen.
 */
export function ensureContrast(foreground: number, background: number, minimum: number): number {
  if (!Number.isFinite(minimum) || minimum < 1) {
    throw new RangeError(`Contrast minimum must be a finite ratio of at least 1, got ${minimum}.`);
  }
  if (contrastRatio(foreground, background) >= minimum) return foreground;

  const toward = contrastRatio(WHITE, background) >= contrastRatio(BLACK, background)
    ? WHITE
    : BLACK;
  // Unreachable: hand back the far end, which is the most contrast that exists
  // against this background. See the note above on why this is not a throw.
  if (contrastRatio(toward, background) < minimum) return toward;

  const start = toHsl(foreground);
  const target = toHsl(toward);
  let low = 0;
  let high = 1;
  for (let step = 0; step < BINARY_SEARCH_STEPS; step += 1) {
    const mix = (low + high) / 2;
    const candidate = fromHsl({
      hue: start.hue,
      saturation: start.saturation,
      lightness: start.lightness + ((target.lightness - start.lightness) * mix),
    });
    if (contrastRatio(candidate, background) >= minimum) high = mix;
    else low = mix;
  }

  return fromHsl({
    hue: start.hue,
    saturation: start.saturation,
    lightness: start.lightness + ((target.lightness - start.lightness) * high),
  });
}
